/**
 * EaselImageField
 *
 * One reusable image field for every Easel editor (journal, collections, site
 * content). Replaces the raw `<Input type="url">` that shipped everywhere with
 * three ways to set an image, plus a live preview and an alt-text input:
 *
 *   1. Upload            — file → POST /easel/media → hosted GCS URL
 *   2. Choose from library — a grid of the artist's own artworks (lifted from
 *                            the collection editor's picker); selecting one sets
 *                            the value to that artwork's preview URL and pre-fills alt
 *   3. Paste URL         — the original escape hatch, http(s) only
 *
 * Controlled: `value`/`alt` come from the parent; every change (including alt
 * edits) is emitted through a single `onChange(url, { alt, source })` so the
 * parent can persist both the URL and the alt text (e.g. imageUrl + imageAlt).
 */

import { useRef, useState } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Upload, Link2, Image as ImageIcon, Loader2, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useMyArtworks, useUploadEaselMedia } from '../hooks'
import { env } from '@/config/env'
import type { EaselMediaKind } from '../types'

type ImageSource = 'upload' | 'library' | 'url'

interface EaselImageFieldProps {
  value?: string
  alt?: string
  onChange: (url: string, meta?: { alt?: string; source: ImageSource }) => void
  kind: EaselMediaKind
  /** e.g. "16:9" — controls the preview box aspect ratio. */
  aspectHint?: string
  /** Optional label above the field. */
  label?: string
  description?: string
}

// Client-side courtesy checks. The server is authoritative on both.
const CLIENT_MAX_BYTES = 10 * 1024 * 1024
const CLIENT_ACCEPT = ['image/jpeg', 'image/png', 'image/webp']

/**
 * Only http(s) URLs are allowed for the paste tab. `javascript:` and `data:`
 * URLs are rejected because these render as <img>/anchors on public storefronts.
 * Mirrors the server-side check.
 */
export function isSafeImageUrl(raw: string): boolean {
  const url = (raw || '').trim()
  if (!url) return false
  // Reject dangerous schemes outright (defensive against whitespace/case tricks).
  if (/^\s*(javascript|data|vbscript|file):/i.test(url)) return false
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

// The library tab stores the picked artwork's preview URL on the record (journal
// imageUrl, collection cover, …), which is then rendered on the artist's
// *cross-origin* public storefront. In production `env.apiUrl` is '' (the admin
// calls its own API via same-origin relative URLs via the Firebase rewrite), so a
// relative preview path would resolve against the storefront's own domain and
// 404. Store an ABSOLUTE URL instead: `env.apiUrl` when set (local dev), else the
// admin's own origin — which in production is the `platform.arthaus.cloud` host
// that fronts the API and CDN-caches it.
function absoluteArtworkPreviewUrl(artworkId: number): string {
  const base = (env.apiUrl && env.apiUrl.trim()) || window.location.origin
  return `${base}/artwork-ms/api/internal/artworks/${artworkId}/preview`
}

const aspectClass = (hint?: string) => {
  switch (hint) {
    case '16:9':
      return 'aspect-video'
    case '1:1':
      return 'aspect-square'
    case '4:3':
      return 'aspect-[4/3]'
    case '3:4':
      return 'aspect-[3/4]'
    default:
      return 'aspect-video'
  }
}

export function EaselImageField({
  value,
  alt,
  onChange,
  kind,
  aspectHint,
  label,
  description,
}: EaselImageFieldProps) {
  const [tab, setTab] = useState<ImageSource>('upload')
  const [urlDraft, setUrlDraft] = useState(value || '')
  const [urlError, setUrlError] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const upload = useUploadEaselMedia()

  const emit = (url: string, source: ImageSource, nextAlt?: string) => {
    onChange(url, { alt: nextAlt ?? alt, source })
  }

  // --- Upload ---------------------------------------------------------------

  const handleFile = (file?: File | null) => {
    setUploadError(null)
    if (!file) return
    if (!CLIENT_ACCEPT.includes(file.type)) {
      setUploadError('Please choose a JPEG, PNG or WEBP image.')
      return
    }
    if (file.size > CLIENT_MAX_BYTES) {
      setUploadError('Image is larger than 10MB.')
      return
    }
    upload.mutate(
      { file, kind },
      {
        onSuccess: (result) => emit(result.url, 'upload'),
        onError: (err) => setUploadError(err.message || 'Upload failed.'),
      }
    )
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    handleFile(e.dataTransfer.files?.[0])
  }

  // --- Paste URL ------------------------------------------------------------

  const applyUrl = () => {
    const trimmed = urlDraft.trim()
    if (!trimmed) {
      setUrlError(null)
      emit('', 'url')
      return
    }
    if (!isSafeImageUrl(trimmed)) {
      setUrlError('Enter a valid http(s) image URL.')
      return
    }
    setUrlError(null)
    emit(trimmed, 'url')
  }

  // --- Render ---------------------------------------------------------------

  return (
    <div className="space-y-3">
      {label && <Label>{label}</Label>}

      <Tabs value={tab} onValueChange={(v) => setTab(v as ImageSource)}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="upload" className="gap-1.5">
            <Upload className="h-3.5 w-3.5" /> Upload
          </TabsTrigger>
          <TabsTrigger value="library" className="gap-1.5">
            <ImageIcon className="h-3.5 w-3.5" /> Library
          </TabsTrigger>
          <TabsTrigger value="url" className="gap-1.5">
            <Link2 className="h-3.5 w-3.5" /> URL
          </TabsTrigger>
        </TabsList>

        {/* Upload */}
        <TabsContent value="upload" className="mt-3">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            disabled={upload.isPending}
            className={cn(
              'flex w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-6 text-center transition-colors',
              dragging ? 'border-primary bg-accent' : 'border-input hover:bg-accent',
              upload.isPending && 'pointer-events-none opacity-70'
            )}
          >
            {upload.isPending ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Uploading…</span>
              </>
            ) : (
              <>
                <Upload className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm font-medium">Drag an image here, or click to browse</span>
                <span className="text-xs text-muted-foreground">JPEG, PNG or WEBP · up to 10MB</span>
              </>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={CLIENT_ACCEPT.join(',')}
            className="hidden"
            onChange={(e) => {
              handleFile(e.target.files?.[0])
              // Allow re-selecting the same file (onChange won't fire otherwise).
              e.target.value = ''
            }}
          />
          {uploadError && <p className="mt-2 text-xs text-destructive">{uploadError}</p>}
        </TabsContent>

        {/* Choose from library */}
        <TabsContent value="library" className="mt-3">
          <LibraryGrid
            onPick={(url, title) => emit(url, 'library', title || alt)}
            selectedUrl={value}
          />
        </TabsContent>

        {/* Paste URL */}
        <TabsContent value="url" className="mt-3 space-y-2">
          <div className="flex gap-2">
            <Input
              type="url"
              placeholder="https://example.com/image.jpg"
              value={urlDraft}
              onChange={(e) => setUrlDraft(e.target.value)}
              onBlur={applyUrl}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  applyUrl()
                }
              }}
            />
            <Button type="button" variant="outline" onClick={applyUrl}>
              Use
            </Button>
          </div>
          {urlError && <p className="text-xs text-destructive">{urlError}</p>}
        </TabsContent>
      </Tabs>

      {/* Live preview + alt (always shown) */}
      <div className="grid gap-3 sm:grid-cols-[minmax(0,180px)_1fr] sm:items-start">
        <div
          className={cn(
            'relative overflow-hidden rounded-lg border bg-muted',
            aspectClass(aspectHint)
          )}
        >
          {value ? (
            <>
              <img
                src={value}
                alt={alt || 'Preview'}
                className="h-full w-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.opacity = '0.15'
                }}
              />
              <button
                type="button"
                aria-label="Remove image"
                onClick={() => {
                  setUrlDraft('')
                  emit('', tab)
                }}
                className="absolute right-1.5 top-1.5 rounded-full bg-background/80 p-1 text-foreground shadow hover:bg-background"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </>
          ) : (
            <div className="flex h-full w-full items-center justify-center text-muted-foreground">
              <ImageIcon className="h-6 w-6 opacity-40" />
            </div>
          )}
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Alt text</Label>
          <Input
            value={alt || ''}
            placeholder="Describe the image for accessibility"
            onChange={(e) => onChange(value || '', { alt: e.target.value, source: tab })}
          />
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// Library grid — the artist's own artworks, reusing the same source + preview
// URL builder the collection editor's picker uses (useMyArtworks +
// artworkApi.getArtworkImageUrl). Selecting sets the value to the artwork's
// public preview URL and hands back its title for the alt field.
// =============================================================================

interface LibraryGridProps {
  onPick: (url: string, title?: string) => void
  selectedUrl?: string
}

function LibraryGrid({ onPick, selectedUrl }: LibraryGridProps) {
  const { data: artworks, isLoading } = useMyArtworks(true)

  if (isLoading) {
    return (
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {[...Array(8)].map((_, i) => (
          <Skeleton key={i} className="aspect-square rounded-md" />
        ))}
      </div>
    )
  }

  if (!artworks || artworks.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        You have no artworks yet. Upload a file or paste a URL instead.
      </p>
    )
  }

  return (
    <div className="grid max-h-[280px] grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-4">
      {artworks.map((art) => {
        const url = absoluteArtworkPreviewUrl(art.id)
        const selected = selectedUrl === url
        return (
          <button
            type="button"
            key={art.id}
            onClick={() => onPick(url, art.name)}
            title={art.name || `Artwork #${art.id}`}
            className={cn(
              'group relative aspect-square overflow-hidden rounded-md border bg-muted transition-all',
              selected ? 'ring-2 ring-primary ring-offset-1' : 'hover:border-primary'
            )}
          >
            <img
              src={url}
              alt={art.name || `Artwork #${art.id}`}
              className="h-full w-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
          </button>
        )
      })}
    </div>
  )
}
